import { Link } from "gatsby"
import React, { useContext } from "react"
import Layout from "../../components/layout"
import Seo from "../../components/seo"
import { StaticImage } from "gatsby-plugin-image"

import styled from "styled-components"

import WebriQForm from "@webriq/gatsby-webriq-form"

const Banner = () => {
  return (
    <>
      <div className="hero-new-study">
        <div className="container">
          <div className="row">
            <div className="col-md-12 mx-auto">
              <div className="title-banner">
                <h1 className="text-center">
                  Custom Quickbase App Tracks 3,000+ Construction Projects for
                  QSR Giant
                </h1>
                <div className="d-flex flex-column flex-sm-row justify-content-center justify-content-sm-start text-center text-sm-left text-white">
                  <p className="pr-sm-1 mb-1 mb-sm-0">
                    <strong>Summary:</strong>
                  </p>
                  <p>
                    Managing 3,000 construction projects pushed internal systems
                    to the brink. Quandary built a custom Quickbase app that
                    flags delays early, tracks site progress, and keeps
                    high-volume rollouts on schedule.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 24px;
  padding: 24px 0;
`

const LogoCard = styled.div`
  background: #fff;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  min-width: 160px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  padding: 12px;
`

const LogoImg = styled.img`
  max-width: 100%;
  max-height: 48px;
  object-fit: contain;
`

const Custom = ({ location }) => {
  return (
    <>
      <Layout banner={<Banner />} location={location}>
        <Seo
          title="Custom Quickbase App Tracks 3,000+ Construction Projects for QSR Giant"
          description="A national restaurant brand faced site-level delays. We built a custom application that let them track numerous projects and surface risks to stay on schedule."
        />
        <div className="py-4 py-md-5 case-studies-inner new-case-studies-inner bg-white">
          <div className="container">
            <div className="row">
              <div className="col-md-4 col-lg-4">
                <div
                  className="sidebar-tab relative results"
                  style={{ position: "sticky", top: "110px" }}
                >
                  <div className="relative results bg-case-study p-4">
                    <h3 className="mt-0 ">Results Snapshot</h3>
                    <ul className="ml-0">
                      <li>600+ construction sites actively tracked</li>
                      <li>Faster detection of project risks</li>
                      <li>Early warnings for site-level delays</li>
                      <li>Shorter customer wait times</li>
                      <li>Safer, smoother daily operations for staff</li>
                    </ul>
                  </div>
                  <a
                    className="btn btn-primary btn-arrow mr-2 mt-4 text-center relative w-full"
                    download
                    target="_blank"
                    href="https://cdn.sanity.io/files/55lcecww/production/ec09bc25e9f0750a2a6ae4badfb7b1124bcaf79a.pdf"
                  >
                    Download This Story
                  </a>
                </div>
              </div>
              <div className="col-md-8 col-lg-8">
                <section className="relative">
                  <div className="relative px-0">
                    <div className="row mx-0">
                      <div className="col-md-11 mx-auto p-4 content-wrapper">
                        <div className="client-content">
                          <h2>The Challenge</h2>
                          <p>
                            Rolling out drive-thru upgrades at over 3,000 stores
                            meant managing thousands of construction projects
                            simultaneously—each with its own dependencies,
                            approvals, and site constraints.
                          </p>
                          <p>
                            The client had piloted{" "}
                            <Link to="/partners/quickbase/">
                              a Quickbase tool
                            </Link>{" "}
                            during a smaller initiative, but scaling it exposed
                            critical limits. Manual workflows broke down.
                            Milestones were missed. And without a clear view
                            into bottlenecks, delays began to snowball.
                          </p>
                          <p>
                            This put the schedules, budgets, and team bandwidth
                            at risk.
                          </p>
                          <p>
                            They needed a way to manage escalation paths,
                            approvals, and task logic at scale before
                            operational complexity overwhelmed their rollout.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-grey">
                        <div className="client-content">
                          <h2>Our Solution</h2>
                          <p>
                            <Link to="/">Quandary Consulting Group</Link> built
                            a custom Quickbase application to help the client
                            manage thousands of simultaneous construction
                            projects, each with its own milestones, approvals,
                            and bottlenecks.
                          </p>
                          <p>
                            <Link to="/custom-quickbase-application-development/">
                              The custom application
                            </Link>{" "}
                            reflected real-world complexity, including project
                            phases, task dependencies, and required executive
                            reviews. It automated milestone forecasting based on
                            actual task completion dates and centralized all
                            project data on a single platform.
                          </p>
                          <p>
                            To handle volume, we introduced nationwide batching.
                            Store operators could volunteer for upgrades, and
                            site data was grouped for streamlined executive
                            review and approval.
                          </p>
                          <p>
                            The system also flagged issues requiring escalation,
                            such as permitting delays and architectural
                            conflicts. This made it easier for teams to identify
                            complications early and maintain consistent data
                            across sites.
                          </p>
                          <div style={{ marginBottom: "30px" }}>
                            <StaticImage
                              src="https://cdn.sanity.io/images/55lcecww/production/138a4d6577aa5e1d58c3291a589d18035129afbd-3676x1322.png"
                              alt="Image 1"
                              className="img-fluid"
                              loading="lazy"
                              placeholder="blurred"
                              style={{
                                objectFit: "cover",
                                height: "100%",
                                marginBottom: "20px",
                              }}
                            />

                            <StaticImage
                              src="https://cdn.sanity.io/images/55lcecww/production/a072c1290723aec57796e4698f4e80913ce0b2d1-688x396.png"
                              alt="Image 2"
                              className="img-fluid"
                              loading="lazy"
                              placeholder="blurred"
                              style={{
                                objectFit: "cover",
                                height: "100%",
                              }}
                            />
                          </div>

                          <p>
                            <strong>Key Features:</strong>
                          </p>
                          <ul className="pl-4">
                            <li>
                              <strong>Nationwide batching: </strong>Sites were
                              grouped to streamline executive approvals and
                              avoid review bottlenecks.
                            </li>
                            <li>
                              <strong>Centralized milestone tracking: </strong>
                              All project data lived in one place, mapped to
                              real-world construction phases.
                            </li>
                            <li>
                              <strong>Automation-assisted workflows: </strong>
                              User updates trigger the next required step,
                              reducing missed tasks and manual follow-up.
                            </li>
                          </ul>
                          <div style={{ marginBottom: "30px" }}>
                            <StaticImage
                              src="https://cdn.sanity.io/images/55lcecww/production/09e4256f955aa2aad2fb53c7ea5e48de40bbd60d-2436x1254.png"
                              alt="Image 2"
                              className="img-fluid"
                              loading="lazy"
                              placeholder="blurred"
                              style={{
                                objectFit: "cover",
                                height: "100%",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>Our Process</h2>
                          <p>
                            The project spanned six months,{" "}
                            <Link to="/our-approach">
                              beginning with a two-month discovery phase
                            </Link>{" "}
                            to understand how construction workflows, approvals,
                            and escalations were executed across thousands of
                            stores. We then translated those processes into a
                            system with built-in logic anda clear user flow.
                          </p>
                          <p>
                            <strong>Key Steps:</strong>
                          </p>
                          <ol className="decimal">
                            <li>
                              <strong>Mapped milestone logic </strong> with the
                              project team to define construction phases, task
                              dependencies, and escalation paths that would
                              drive the system
                            </li>
                            <li>
                              <strong>Visualized workflows </strong> through
                              diagrams and user flows to align stakeholders and
                              guide development
                            </li>
                            <li>
                              <strong> Migrated and normalized data </strong>
                              using custom scripts to restructure permits,
                              contracts, and legacy records into the new system
                            </li>
                            <li>
                              <strong>Developed and tested automations </strong>
                              that triggered tasks and deadlines based on real
                              project behavior
                            </li>
                            <li>
                              <strong> Ran hands-on user testing </strong>
                              with internal teams to surface friction points and
                              refine usability ahead of launch
                            </li>
                          </ol>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-gradient">
                        <div className="client-content">
                          <h2 className="text-white">The Roadblocks</h2>
                          <p className="text-white">
                            One major challenge was the state of the legacy
                            data. The client’s previous workflow relied heavily
                            on manual entry, and key fields such as task
                            predecessors were often left blank or inconsistently
                            filled out.
                          </p>
                          <p className="text-white">
                            This made it difficult to build reliable logic or
                            surface historical patterns. To resolve this, we
                            worked closely with the client’s team to identify
                            gaps, clean records, and reestablish milestone
                            relationships.
                          </p>
                          <p className="text-white">
                            This hands-on effort was critical to stabilizing the
                            new system and to avoiding errors that could derail
                            thousands of active projects.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>The Toolstack</h2>
                          <LogoGrid>
                            <LogoCard>
                              <LogoImg
                                src="https://cdn.sanity.io/images/55lcecww/production/78673169bfad856d32e060c5bf029890db184c60-261x101.png"
                                alt="Quickbase"
                              />
                            </LogoCard>
                          </LogoGrid>
                          <ul className="pl-4">
                            <li>
                              <strong>Quickbase: </strong>
                              Used to model real-world construction workflows
                              without starting from scratch. Its low-code
                              flexibility enabled us to automate milestone
                              logic, manage escalations, and centralize site
                              data at a scale that supported thousands of
                              projects.
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-blue">
                        <div className="client-content">
                          <h2 className="text-white">The Results</h2>
                          <p className="text-white">
                            The system now tracks over 3,000 projects across 600
                            active construction sites, giving operations full
                            visibility from planning through completion.
                          </p>
                          <p className="text-white">
                            Teams no longer rely on scattered spreadsheets.
                            Milestones, escalations, and site statuses are
                            centralized, making it easier to spot issues early
                            and avoid cascading delays.
                          </p>
                          <p className="text-white">
                            <Link
                              style={{
                                color: "white",
                                textDecoration: "underline",
                                transition: "color 0.3s ease",
                              }}
                              to="/quickbase-integration-services"
                            >
                              With standardized workflows and historical data
                            </Link>{" "}
                            in one place, leadership can pinpoint recurring
                            bottlenecks, underperforming contractors, and
                            high-risk sites.
                          </p>
                          <p className="text-white">
                            At the store level, upgraded drive-thrus have
                            increased throughput, reduced wait times, and
                            simplified daily operations for on-site teams.
                          </p>
                          <p>
                            <strong className="text-white">Key Results:</strong>
                          </p>
                          <ol className="decimal text-white">
                            <li className="text-white">
                              600+ construction sites tracked in one system
                            </li>
                            <li className="text-white">
                              Faster detection of delays and bottlenecks
                            </li>
                            <li className="text-white">
                              Recurring contractor issues flagged early
                            </li>
                            <li className="text-white">
                              Shorter customer wait times
                            </li>
                            <li className="text-white">
                              Smoother workflows for on-site staff
                            </li>
                          </ol>
                        </div>
                      </div>
                      <div
                        className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-grey"
                        style={{ marginTop: "30px" }}
                      >
                        <div className="client-content">
                          <h2>Client Overview:</h2>
                          <p>
                            The client is a major U.S. restaurant brand with
                            thousands of locations nationwide. Their operations
                            team oversees store development, construction, and
                            upgrades at all sites.
                          </p>
                          <div className="row align-items-center">
                            <div className="pl-4">
                              <p>
                                <strong>Industry:</strong> Quick-service
                                restaurant (QSR)
                                <span className="relative d-block">
                                  <strong>Location:</strong> United States
                                </span>
                                <span className="relative d-block">
                                  <strong>Size:</strong> 3,000+ U.S. locations
                                </span>
                              </p>
                            </div>

                            {/* <div className="col-md-6 pl-md-4 d-flex justify-content-center align-items-center border-left-md">
                              <LogoGrid>
                                <LogoCard>
                                  <LogoImg
                                    src="https://cdn.sanity.io/images/55lcecww/production/d4897c4eff3af5ae876b523c7a21bdfeffdd0e4c-1575x357.png"
                                    alt="Quickbase"
                                  />
                                </LogoCard>
                              </LogoGrid>
                            </div> */}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto px-0 pt-4 content-wrapper">
                        <div className="client-content">
                          <div id="speak-with-specialist">
                            <div className="form-wrap-contact">
                              <div className="form-wrapper">
                                <div className="intro mb-2">
                                  <div className="row">
                                    <div className="col-md-12">
                                      <h2 className="mt-0 font-weight-medium">
                                        Speak with Our Innovation Team
                                      </h2>
                                      <p>
                                        Use the form below to drop us an e-mail.
                                        We'll be in touch shortly.
                                      </p>
                                      <small className="text-red">
                                        Fields marked with an asterisk (*) are
                                        required.
                                      </small>
                                    </div>
                                  </div>
                                </div>
                                <WebriQForm
                                  id="contact-form"
                                  name="Contact Form"
                                  className="contactForm"
                                  data-form-id="4d46fe65-4d66-46b4-946c-e5826349ed3c"
                                  data-thankyou-url="/thank-you"
                                >
                                  <div className="row">
                                    <div className="col-lg-6 col-md-12 col-sm-12">
                                      {/* <p class="required">Full Name</p> */}

                                      <input
                                        type="text"
                                        name="Full Name"
                                        size={40}
                                        className="form-control required"
                                        placeholder="Full Name *"
                                        aria-label="Full Name"
                                        required
                                        id="requiredField"
                                      />
                                    </div>
                                    <div className="col-lg-6 col-md-12 col-sm-12 mt-4 mt-lg-0">
                                      {/* <p class="required">Phone</p> */}
                                      <input
                                        type="text"
                                        name="Phone"
                                        size={40}
                                        // minLength={14}
                                        className="form-control required"
                                        id="phone"
                                        placeholder="Phone Number *"
                                        aria-label="Phone"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">Email</p> */}
                                      <input
                                        type="email"
                                        name="Email"
                                        size={40}
                                        className="form-control"
                                        aria-label="Email"
                                        placeholder="Email *"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">Message</p> */}
                                      <textarea
                                        name="Message"
                                        rows={5}
                                        cols={0}
                                        className="form-control"
                                        aria-label="Message"
                                        placeholder="Message *"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">How did you find out about us?</p> */}
                                      <div>
                                        <textarea
                                          type="text"
                                          name="How did you find out about us?"
                                          size={40}
                                          rows={5}
                                          className="form-control"
                                          aria-label="How did you find out about us?"
                                          placeholder="How did you find out about us? *"
                                          required
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="row mt-3">
                                    <div className="form-group col-12 d-flex justify-content-start captcha-wrap">
                                      <div className="webriq-recaptcha" />
                                    </div>
                                    <div className="form-group col-12 text-left">
                                      <div className="input-filled">
                                        <button
                                          type="submit"
                                          className="btn btn-primary btn-arrow"
                                        >
                                          SUBMIT
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </WebriQForm>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  )
}

export default Custom
